import { useEffect } from 'react';
/**
 * Optional, minimal smart scroll behavior (progressive enhancement).
 * - Adds 'with-smart-scroll' to .main-content for CSS scoping
 * - Toggles 'filters-hidden' on .main-content based on scroll direction
 * - Maintains CSS variables --app-header-height and --filters-height via ResizeObserver
 * - Input area visibility controlled by adding/removing 'input-hidden' on the wrapper
 */
const useSmartScroll = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;

    const appRoot = (document.querySelector('.App.mobile-app') || document.querySelector('.App')) as HTMLElement | null;
    const DEBUG = false;
    const mainContent = document.querySelector('.main-content') as HTMLElement | null;

    // With layout.css enforcing .main-content as the sole scroller, use it directly
    const scrollContainer: HTMLElement | null = mainContent;
    const headerEl = document.querySelector('.mobile-header') as HTMLElement | null;
    const titleEl = (document.querySelector('.mobile-header .app-title-pill') ||
                    document.querySelector('.mobile-header .header-title')) as HTMLElement | null;
    const getInputs = () => Array.from(document.querySelectorAll('.page-template-input')) as HTMLElement[];

    // mark content for CSS rules
    if (mainContent) mainContent.classList.add('with-smart-scroll');

    // keep header/input heights as CSS vars
    let prevGI = -1;
    let correctionPasses = 0;
    // Identify Inventory page (no special bypass; keep smart scroll enabled)
    const isInventory = !!document.querySelector('.page-template.inventory-page');
    const setVars = () => {
      // Use visual header height only
      const rect = headerEl ? headerEl.getBoundingClientRect() : null;
      const h = rect ? Math.ceil(rect.height) : (headerEl?.offsetHeight || 60);
      // Measure the title offset within the header to pin input just below it
      const tRect = titleEl ? titleEl.getBoundingClientRect() : null;
      const th = tRect ? Math.ceil(tRect.height) : 0;
      const toffset = (rect && tRect) ? Math.ceil(tRect.bottom - rect.top) : th; // bottom of title within header
      const tTopOffset = (rect && tRect) ? Math.max(0, Math.ceil(tRect.top - rect.top)) : 0; // top of title within header
      // Measure PageTemplate header (in-content) to align sticky input beneath it in PWA
      const pageHeader = document.querySelector('.page-template .page-template-header') as HTMLElement | null;
      const phRect = pageHeader ? pageHeader.getBoundingClientRect() : null;
      const ph = phRect ? Math.ceil(phRect.height) : (pageHeader?.offsetHeight || 0);
      const first = getInputs()[0];
      const ih = first?.offsetHeight || 0;
      // Visual gap between the element above and the input wrapper top
      // Prefer PageTemplate header bottom -> input top; otherwise the APP TITLE bottom (TO) -> input top
      const gi = (() => {
        const iRect = first ? first.getBoundingClientRect() : null;
        if (!iRect) return 0;
        if (phRect) return Math.max(0, Math.ceil(iRect.top - phRect.bottom));
        if (rect && tRect) return Math.max(0, Math.ceil(iRect.top - rect.top - toffset));
        return 0;
      })();
      document.documentElement.style.setProperty('--app-header-height', `${h}px`);
      document.documentElement.style.setProperty('--page-title-height', `${th}px`);
      document.documentElement.style.setProperty('--page-title-offset', `${toffset}px`);
      document.documentElement.style.setProperty('--page-header-height', `${ph}px`);
      document.documentElement.style.setProperty('--filters-height', `${ih}px`);
      // Expose visual gap between title and input so CSS can correct it precisely
      document.documentElement.style.setProperty('--input-gap', `${gi}px`);
      // Reserve space via CSS only. Avoid inline padding adjustments on Inventory.
      if (!isInventory) {
        const mc = mainContent;
        let pt = 0;
        if (mc) {
          const cs = getComputedStyle(mc);
          const ptt = cs.paddingTop || '0px';
          pt = parseInt(ptt, 10) || 0;
          const desiredPT = Math.max(0, toffset);
          if (Math.abs(pt - desiredPT) > 1) {
            mc.style.paddingTop = `${desiredPT}px`;
          }
        }
      }

      // Inventory: rely on CSS-only sticky positioning to avoid jitter; no inline adjustments
      // Iterate a few frames to converge; stop when GI <= 1 or after 10 passes
      if (gi > 1 && correctionPasses < 10) {
        correctionPasses += 1;
        prevGI = gi;
        requestAnimationFrame(() => setVars());
      }

      // Debug removed
    };
    setVars();
    let ro: ResizeObserver | null = null;
    const onResize = () => setVars();
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(setVars);
      if (headerEl) ro.observe(headerEl);
      const inputs = getInputs(); inputs.forEach(i => ro!.observe(i));
      const pageHeader = document.querySelector('.page-template .page-template-header') as HTMLElement | null;
      if (pageHeader) ro.observe(pageHeader);
    } else {
      (window as any).addEventListener('resize', onResize);
    }

    // helpers
    let hidden = false;
    const show = () => {
      mainContent?.classList.remove('filters-hidden');
      const inputs = getInputs();
      inputs.forEach(i => {
        i.classList.remove('input-hidden');
        i.style.transform = '';
        i.style.opacity = '';
        i.style.pointerEvents = '';
      });
      hidden = false;
      // Removed previous mid-list scroll nudges to avoid timing races; stabilizers handle this now
      // debug log removed
      // Re-run measurement and correction after UI becomes visible
      correctionPasses = 0; prevGI = -1; setVars();
    };
    const hide = () => {
      mainContent?.classList.add('filters-hidden');
      const inputs = getInputs();
      const fh = getComputedStyle(document.documentElement).getPropertyValue('--filters-height').trim() || '56px';
      inputs.forEach(i => {
        i.classList.add('input-hidden');
        i.style.transform = `translate3d(0, calc(-1 * ${fh}), 0)`;
        i.style.opacity = '0';
        i.style.pointerEvents = 'none';
      });
      hidden = true;
      // debug log removed
      // Re-run after state change to maintain consistent variables
      correctionPasses = 0; prevGI = -1; setVars();
    };

    // Sync state: keep header always visible; only maintain variables
    const syncToHeader = () => {
      correctionPasses = 0; prevGI = -1; setVars();
    };
    // Run once on init
    // Initialize visible; then allow scroll handlers to control filters visibility
    show();
    syncToHeader();
    // Observe class changes
    const classObserver = new MutationObserver(() => syncToHeader());
    if (appRoot) classObserver.observe(appRoot, { attributes: true, attributeFilter: ['class'] });

    // === Scroll direction detection: hide on down, show on up ===
    let lastScrollY = 0;
    let ticking = false;
    let hiddenState = false;
    let accumUp = 0;
    let accumDown = 0;
    const HIDE_AFTER = 2; // px cumulative
    const SHOW_AFTER = 8; // px cumulative
    let lastToggleAt = 0; // ms
    const TOGGLE_COOLDOWN_MS = 220;
    // Stabilizers
    const NEAR_TOP_PX = 40;
    const SETTLE_MS = 300;
    let lastActionAt = 0; // time of last show/hide
    const NEAR_BOTTOM_PX = 20; // keep controls shown near the end to avoid jitter/clipping

    const isAtBottom = (): boolean => {
      if (!scrollContainer) return false;
      const el = scrollContainer;
      const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
      return remaining <= NEAR_BOTTOM_PX; // treat within 120px of end as bottom zone
    };

    const getCurrentScroll = (): number => {
      return (scrollContainer?.scrollTop || 0);
    };

    const handleScroll = (_event: Event) => {
      const scrollY = getCurrentScroll();
      if (!ticking) {
        requestAnimationFrame(() => {
          const current = scrollY;
          // Guard: if near the bottom, keep filters hidden so more cards stay visible
          if (isAtBottom()) {
            if (!hiddenState) {
              const now = Date.now();
              if (now - lastToggleAt > TOGGLE_COOLDOWN_MS) {
                hide();
                hiddenState = true;
                lastToggleAt = now;
              }
            }
            accumUp = 0; accumDown = 0;
            lastScrollY = current;
            ticking = false;
            return;
          }
          // Near-top lock: force SHOW and suppress HIDE jitter close to the top
          if (current <= NEAR_TOP_PX) {
            show();
            hiddenState = false;
            accumUp = 0; accumDown = 0;
            lastScrollY = current;
            lastActionAt = Date.now();
            ticking = false;
            return;
          }
          const delta = current - lastScrollY;

          // Force show at/above top (handles iOS rubber-band)
          if (current <= 0) {
            show();
            hiddenState = false;
            accumUp = 0; accumDown = 0;
            lastScrollY = 0;
            ticking = false;
            return;
          }

          if (Math.abs(delta) >= 3) {
            if (delta > 0) {
              // scrolling down
              accumDown += delta; accumUp = 0;
              if (!hiddenState && accumDown >= HIDE_AFTER) {
                const now = Date.now();
                if ((now - lastToggleAt > TOGGLE_COOLDOWN_MS) && (now - lastActionAt > SETTLE_MS)) {
                  hide();
                  hiddenState = true;
                  lastToggleAt = now;
                  lastActionAt = now;
                }
                accumDown = 0;
              }
            } else {
              // scrolling up
              accumUp += -delta; accumDown = 0;
              if (hiddenState && accumUp >= SHOW_AFTER) {
                const now = Date.now();
                if ((now - lastToggleAt > TOGGLE_COOLDOWN_MS) && (now - lastActionAt > SETTLE_MS)) {
                  show();
                  hiddenState = false;
                  lastToggleAt = now;
                  lastActionAt = now;
                }
                accumUp = 0;
              }
            }
          }

          lastScrollY = current;
          ticking = false;
        });
        ticking = true;
      }
    };

    // Attach listener only to the designated scroll container
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    // Initialize lastScrollY from the best current source
    lastScrollY = getCurrentScroll();

    // init debug removed

    // Touch listeners to react during finger move (iOS PWA)
    let touchActive = false;
    const onTouchStart = () => { touchActive = true; };
    const onTouchMove = () => {
      const current = getCurrentScroll();
      if (current <= 0) {
        show();
        hiddenState = false;
        accumUp = 0; accumDown = 0;
        lastScrollY = 0;
        lastActionAt = Date.now();
        return;
      }
      if (current <= NEAR_TOP_PX) {
        show();
        hiddenState = false;
        accumUp = 0; accumDown = 0;
        lastScrollY = current;
        lastActionAt = Date.now();
        return;
      }
      if (isAtBottom()) {
        // At bottom during touch, keep filters hidden for maximum card visibility
        if (!hiddenState) {
          const now = Date.now();
          if ((now - lastToggleAt > TOGGLE_COOLDOWN_MS) && (now - lastActionAt > SETTLE_MS)) {
            hide();
            hiddenState = true;
            lastToggleAt = now;
            lastActionAt = now;
          }
        }
        accumUp = 0; accumDown = 0;
        lastScrollY = current;
        return;
      }
      const delta = current - lastScrollY;
      if (Math.abs(delta) < 3) return;
      if (delta > 0) {
        accumDown += delta; accumUp = 0;
        if (!hiddenState && accumDown >= HIDE_AFTER) {
          const now = Date.now();
          if ((now - lastToggleAt > TOGGLE_COOLDOWN_MS) && (now - lastActionAt > SETTLE_MS)) {
            hide();
            hiddenState = true;
            lastToggleAt = now;
            lastActionAt = now;
          }
          accumDown = 0;
        }
      } else {
        accumUp += -delta; accumDown = 0;
        if (hiddenState && accumUp >= SHOW_AFTER) {
          const now = Date.now();
          if ((now - lastToggleAt > TOGGLE_COOLDOWN_MS) && (now - lastActionAt > SETTLE_MS)) {
            show();
            hiddenState = false;
            lastToggleAt = now;
            lastActionAt = now;
          }
          accumUp = 0;
        }
      }
      lastScrollY = current;
    };
    const onTouchEnd = () => { touchActive = false; };
    if (scrollContainer) {
      scrollContainer.addEventListener('touchstart', onTouchStart, { passive: true });
      scrollContainer.addEventListener('touchmove', onTouchMove, { passive: true });
      scrollContainer.addEventListener('touchend', onTouchEnd, { passive: true });
      scrollContainer.addEventListener('touchcancel', onTouchEnd, { passive: true });
    }

    // Initialize last position
    lastScrollY = getCurrentScroll();

    return () => {
      mainContent?.classList.remove('with-smart-scroll');
      mainContent?.classList.remove('filters-hidden');
      appRoot?.classList.remove('smart-hidden');
      classObserver.disconnect();
      if (ro) ro.disconnect(); else (window as any).removeEventListener('resize', onResize);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll as any);
        scrollContainer.removeEventListener('touchstart', onTouchStart as any);
        scrollContainer.removeEventListener('touchmove', onTouchMove as any);
        scrollContainer.removeEventListener('touchend', onTouchEnd as any);
        scrollContainer.removeEventListener('touchcancel', onTouchEnd as any);
      }
    };
  }, [enabled]);
};

export default useSmartScroll;
